import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { FaMapMarkerAlt, FaSearch, FaTimes } from 'react-icons/fa';

export default function DistanceFilter({ onFilterChange }) {
  const { i18n } = useTranslation();
  const [city, setCity] = useState('');
  const [radius, setRadius] = useState([200]); // Rayon par défaut: 200 km (plus grand pour international)
  const [isFilterActive, setIsFilterActive] = useState(false);

  const handleCityChange = (e) => {
    setCity(e.target.value);
  };

  const handleRadiusChange = (value) => {
    setRadius(value);
  };

  // Appliquer le filtre uniquement quand l'utilisateur clique sur le bouton
  const handleApplyFilter = () => {
    if (city.trim()) {
      onFilterChange(city.trim(), radius[0]);
      setIsFilterActive(true);
    }
  };

  // Réinitialiser le filtre
  const handleClearFilter = () => {
    setCity('');
    setRadius([200]);
    setIsFilterActive(false);
    onFilterChange('', null);
  };

  // Permettre la recherche avec Entrée
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && city.trim()) {
      handleApplyFilter();
    }
  };

  const radiusOptions = [50, 100, 200, 500, 1000, 2000];

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FaMapMarkerAlt className="text-[#0A2540]" />
          <h3 className="font-semibold text-[#0A2540]">
            {i18n.language === 'fr' ? 'Filtrer par distance' : 'Filter by distance'}
          </h3>
        </div>
        {isFilterActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilter}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <FaTimes className="mr-1" />
            {i18n.language === 'fr' ? 'Effacer' : 'Clear'}
          </Button>
        )}
      </div>

      {/* Ville de recherche */}
      <div className="mb-4">
        <Label htmlFor="city" className="text-sm mb-2 block">
          {i18n.language === 'fr' ? 'Ville / Localisation' : 'City / Location'}
        </Label>
        <div className="flex gap-2">
          <Input
            id="city"
            type="text"
            placeholder={i18n.language === 'fr' ? 'Ex: Paris, Casablanca, Dubai...' : 'Ex: Paris, Casablanca, Dubai...'}
            value={city}
            onChange={handleCityChange}
            onKeyPress={handleKeyPress}
            className="flex-1"
          />
          <Button
            onClick={handleApplyFilter}
            disabled={!city.trim()}
            className="bg-[#635BFF] hover:bg-[#5248d9]"
          >
            <FaSearch />
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {i18n.language === 'fr' 
            ? 'Entrez une ville puis cliquez sur rechercher ou appuyez sur Entrée' 
            : 'Enter a city then click search or press Enter'}
        </p>
      </div>

      {/* Slider de rayon */}
      <div className="mb-2">
        <Label htmlFor="radius" className="text-sm mb-2 block">
          {i18n.language === 'fr' ? 'Rayon de recherche' : 'Search radius'}: 
          <span className="font-bold text-[#635BFF] ml-2">{radius[0]} km</span>
        </Label>
        
        <Slider
          id="radius"
          min={50}
          max={2000}
          step={50}
          value={radius}
          onValueChange={handleRadiusChange}
          className="w-full"
        />
        
        {/* Indicateurs de distance */}
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>50 km</span>
          <span>500 km</span>
          <span>2000 km</span>
        </div>
      </div>

      {/* Boutons rapides */}
      <div className="flex flex-wrap gap-2 mt-3">
        {radiusOptions.map((r) => (
          <button
            key={r}
            onClick={() => {
              setRadius([r]);
              if (city.trim() && isFilterActive) {
                onFilterChange(city.trim(), r);
              }
            }}
            className={`px-3 py-1 text-xs rounded-full transition cursor-pointer ${
              radius[0] === r
                ? 'bg-[#635BFF] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {r} km
          </button>
        ))}
      </div>

      {isFilterActive && city && (
        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
          <FaMapMarkerAlt className="inline mr-1" />
          {i18n.language === 'fr' 
            ? `Recherche active : ${radius[0]} km autour de "${city}"` 
            : `Active search: ${radius[0]} km radius from "${city}"`}
        </div>
      )}
    </div>
  );
}
